import React, { FC } from 'react';
import { Box, Flex, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import { ILayoutProps } from './BaseLayout';
import { bannerAnimations, getAnimationProps } from './animations';

const BannerLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'lg' as const }) ?? 'sm';

    return (
        <Box
            position="absolute"
            width="full"
            height="full"
            background={colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white}
            // Stacking context so CoverPhoto's `z-index: -1` can't escape past this opaque
            // background and hide the cover — see the fuller note in BlockLeftLayout.
            isolation="isolate"
            onClick={onClick}
        >
            {/* Desktop Layout */}
            <Box
                display={{ base: 'none', lg: 'block' }}
                position="relative"
                width="full"
                height="full"
                overflow="hidden"
            >
                {/* Cover Image - Desktop */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '85%',
                        height: '65%',
                        left: '7.5%',
                        top: '12%'
                    }}
                    {...getAnimationProps(bannerAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>

                {/* Gallery Info - Desktop */}
                <Box position="absolute" left="7.5%" bottom="8%" width="85%">
                    <Flex justifyContent="space-between" alignItems="flex-end" width="full">
                        {/* Content - aligned with cover image left edge */}
                        <Flex flexDirection="column" gap="16px" alignItems="flex-start">
                            <motion.div
                                {...getAnimationProps(
                                    bannerAnimations.galleryName,
                                    enableAnimations
                                )}
                            >
                                <GalleryHeading
                                    size={galleryHeadingSize}
                                    text={galleryTitle}
                                    variant="primary"
                                />
                            </motion.div>
                            <motion.div
                                {...getAnimationProps(
                                    bannerAnimations.studioName,
                                    enableAnimations
                                )}
                            >
                                <StudioHeading text={brandName} variant="primary" />
                            </motion.div>
                        </Flex>

                        {/* Button - aligned with cover image right edge */}
                        <motion.div
                            {...getAnimationProps(bannerAnimations.viewBtn, enableAnimations)}
                        >
                            <Button
                                text={buttonText}
                                textLarge={buttonTextLarge}
                                variant="outline primary"
                            />
                        </motion.div>
                    </Flex>
                </Box>
            </Box>

            {/* Mobile Layout */}
            <Flex
                display={{ base: 'flex', lg: 'none' }}
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="flex-start"
                width="full"
                height="full"
                padding="24px"
                overflow="hidden"
            >
                {/* Cover Image - Mobile (max 60% height) */}
                <motion.div
                    style={{
                        width: '100%',
                        maxHeight: '60%',
                        aspectRatio: '4/3',
                        flexShrink: 0,
                        marginBottom: '32px'
                    }}
                    {...getAnimationProps(bannerAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>

                {/* Content Section - Mobile (left aligned) */}
                <Flex flexDirection="column" gap="24px" alignItems="flex-start" width="full">
                    {/* Gallery Name */}
                    <motion.div
                        {...getAnimationProps(bannerAnimations.galleryName, enableAnimations)}
                    >
                        <GalleryHeading
                            size={galleryHeadingSize}
                            text={galleryTitle}
                            variant="primary"
                        />
                    </motion.div>

                    {/* Studio Name (never shows brand logo) */}
                    <motion.div
                        {...getAnimationProps(bannerAnimations.studioName, enableAnimations)}
                    >
                        <StudioHeading text={brandName} variant="primary" />
                    </motion.div>

                    {/* Button (not full width) */}
                    <motion.div {...getAnimationProps(bannerAnimations.viewBtn, enableAnimations)}>
                        <Button
                            text={buttonText}
                            textLarge={buttonTextLarge}
                            variant="outline primary"
                        />
                    </motion.div>
                </Flex>
            </Flex>
        </Box>
    );
};

export default BannerLayout;
